import React from 'react';
import type { LucideIcon } from 'lucide-react';

// ---------------------------------------------------------------------------
// Design primitives (dashboard dark-glass style)
// ---------------------------------------------------------------------------

/*** Glass section card — matches landing page feature cards */
export function Section({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl bg-panel border border-line overflow-hidden">
      <div className="px-5 py-4 border-b border-line flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-accent-700 dark:text-accent-300">{title}</h3>
          {description && <p className="mt-0.5 text-xs text-ink-muted leading-relaxed">{description}</p>}
        </div>
        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>
      <div className="px-5 py-5">{children}</div>
    </div>
  );
}

/*** Dark glass text field */
export function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  icon: Icon,
  disabled,
  maxLength,
  autoComplete,
  name,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  icon?: LucideIcon;
  disabled?: boolean;
  maxLength?: number;
  autoComplete?: string;
  name?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-ink-muted">{label}</label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-faint pointer-events-none" />
        )}
        <input
          type={type}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          autoComplete={autoComplete ?? 'off'}
          className={`w-full rounded-xl bg-raised border border-line-strong text-ink text-sm
                      placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50
                      focus:ring-1 focus:ring-accent-500/20 transition-all py-2.5
                      disabled:opacity-50 disabled:cursor-not-allowed
                      ${Icon ? 'pl-9 pr-3' : 'px-3'}`}
        />
      </div>
    </div>
  );
}

/*** Dark glass textarea */
export function FieldArea({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-ink-muted">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full rounded-xl bg-raised border border-line-strong text-ink text-sm
                   placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50
                   focus:ring-1 focus:ring-accent-500/20 transition-all px-3 py-2.5 resize-none"
      />
    </div>
  );
}

/*** Dark glass select */
export function FieldSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-ink-muted">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl bg-raised border border-line-strong text-ink text-sm
                   focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20
                   transition-all px-3 py-2.5 appearance-none cursor-pointer
                   [&>option]:bg-panel [&>option]:text-ink"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

/*** Dark glass toggle */
export function FieldToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-4 cursor-pointer group">
      <div className="relative flex-shrink-0 mt-0.5">
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <div
          className={`w-11 h-6 rounded-full transition-all duration-200 ${
            checked
              ? 'bg-accent-600'
              : 'bg-line-strong group-hover:bg-ink-faint'
          }`}
        >
          <div
            className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
              checked ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </div>
      </div>
      <div className="min-w-0">
        <span className="block text-sm font-medium text-ink">{label}</span>
        {description && (
          <span className="block text-xs text-ink-muted mt-0.5 leading-relaxed">{description}</span>
        )}
      </div>
    </label>
  );
}
